import { Check, ChevronDown, Filter } from "lucide-react"
import { type ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"

import { MasterclassCompletionField } from "@/components/crew/masterclass-completion-field"
import { DebouncedSearchInput } from "@/components/shared/debounced-search-input"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Toggle } from "@/components/ui/toggle"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import {
  CREW_SORT_OPTIONS,
  type CrewFilterOptions,
  type CrewFilters,
  type CrewSort,
  type MasterclassCompletionOp,
  resolveCrewSortOption,
} from "@/types/crew"

/**
 * OKS-25 F6 — Crew Roster filter rail.
 *
 * Desktop ≥640px: inline row — Search · Location · Followers · Tags
 * · Joined · Sort · Clear all.
 *
 * Mobile <640px: collapsed behind a "Filters" button (`crew-list-filter-
 * button`) that opens a `Sheet` (`crew-list-filter-mobile-sheet`)
 * containing the same filters stacked vertically.
 *
 * Search debounces 300ms (`<DebouncedSearchInput>`); Location / Tags /
 * Sort write the URL instantly on selection; Followers and Joined ranges
 * commit on `Apply` inside their respective Popovers (avoids
 * back-button polluting from per-keystroke writes).
 *
 * **Wire-format canary** — every form-shaped input carries a literal
 * `name` attribute matching the BE strong-params key (`q`, `location`,
 * `tags[]`, `followers_min`, `followers_max`, `joined_from`,
 * `joined_to`). Vitest asserts these literals so a Rails strong-
 * params drift surfaces here before Playwright (memory
 * `vitest_wire_format_canary`).
 *
 * Locked testids match the FE TRD §testid contract; see also QA TRD v5.
 */
interface CrewFilterRailProps {
  filters: CrewFilters
  sort: CrewSort
  filterOptions: CrewFilterOptions
  onLocationChange: (location: string | null) => void
  onFollowersChange: (min: number | null, max: number | null) => void
  onTagsChange: (tags: string[]) => void
  onJoinedRangeChange: (from: string | null, to: string | null) => void
  onSearchChange: (q: string | null) => void
  /**
   * OKS-27 — At-risk lifecycle toggle. URL wire format `?at_risk=1`
   * (NOT `=true`) — Vitest URL canary asserts the literal `1`. Echoed
   * back as `filters.at_risk: bool`. Server-scoped union of rotation
   * OR inactivity sets.
   */
  onAtRiskChange: (value: boolean) => void
  /**
   * OKS-58 — Masterclass-completion rule pair. Both args travel
   * together; emit `(null, null)` to clear. Parent (`CrewIndex`)
   * routes both to `useCrewListUrl.push` in a single call so the URL
   * mutation is atomic.
   */
  onMasterclassCompletionChange: (
    op: MasterclassCompletionOp | null,
    masterclassId: number | null,
  ) => void
  onSortChange: (sort: CrewSort) => void
  onClearAll: () => void
  /**
   * OKS-28 §5 (responsive) — at <640px, the segment selector collapses
   * into the mobile filter Sheet as the FIRST row, above the filter
   * fields. Provided by `Brand/Crew/Index.tsx`; rail is otherwise
   * segment-agnostic. Desktop renders its own copy above the rail.
   */
  mobileSheetTopSlot?: ReactNode
  /**
   * OKS-28 §5 (responsive) — at <640px, the Save-as-segment action
   * lives inside the Sheet footer as a full-width button. Show/hide
   * predicate is owned by the slot itself (`<SaveSegmentButton>` —
   * returns null when there's nothing to save). Footer self-hides via
   * the `:empty` pseudo when the slot resolves to null.
   */
  mobileSheetFooterSlot?: ReactNode
}

export function CrewFilterRail(props: CrewFilterRailProps) {
  const isMobile = useIsMobile()
  const { t } = useTranslation()

  const fields = (
    <FilterFields
      filters={props.filters}
      sort={props.sort}
      filterOptions={props.filterOptions}
      onLocationChange={props.onLocationChange}
      onFollowersChange={props.onFollowersChange}
      onTagsChange={props.onTagsChange}
      onJoinedRangeChange={props.onJoinedRangeChange}
      onAtRiskChange={props.onAtRiskChange}
      onMasterclassCompletionChange={props.onMasterclassCompletionChange}
      onSortChange={props.onSortChange}
    />
  )

  if (isMobile) {
    return (
      <div data-testid="crew-list-filter-bar" className="flex flex-col gap-2">
        <DebouncedSearchInput
          defaultValue={props.filters.q ?? ""}
          onDebouncedChange={(v) => props.onSearchChange(v ? v : null)}
          testId="crew-list-search"
          ariaLabel={t("crew.list.search_aria")}
          placeholder={t("crew.list.search_placeholder")}
          name="q"
        />
        <div className="flex items-center gap-2">
          <Sheet>
            <SheetTrigger
              render={
                <Button
                  type="button"
                  variant="outline"
                  data-testid="crew-list-filter-button"
                >
                  <Filter className="size-4" aria-hidden="true" />
                  {t("crew.filters.mobile_button_label")}
                </Button>
              }
            />
            <SheetContent
              side="right"
              data-testid="crew-list-filter-mobile-sheet"
            >
              <SheetHeader>
                <SheetTitle>{t("crew.filters.mobile_sheet_title")}</SheetTitle>
                <SheetDescription className="sr-only">
                  {t("crew.filters.mobile_sheet_title")}
                </SheetDescription>
              </SheetHeader>
              <div className="flex flex-col gap-4 p-4">
                {props.mobileSheetTopSlot}
                {fields}
              </div>
              <SheetFooter className="[&:empty]:hidden">
                {props.mobileSheetFooterSlot}
              </SheetFooter>
            </SheetContent>
          </Sheet>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="crew-list-filter-clear"
            onClick={props.onClearAll}
          >
            {t("crew.filters.clear")}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid="crew-list-filter-bar"
      className="flex flex-wrap items-center gap-2"
    >
      <DebouncedSearchInput
        defaultValue={props.filters.q ?? ""}
        onDebouncedChange={(v) => props.onSearchChange(v ? v : null)}
        testId="crew-list-search"
        ariaLabel={t("crew.list.search_aria")}
        placeholder={t("crew.list.search_placeholder")}
        name="q"
        className="w-full sm:max-w-xs"
      />
      {fields}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        data-testid="crew-list-filter-clear"
        onClick={props.onClearAll}
      >
        {t("crew.filters.clear")}
      </Button>
    </div>
  )
}

// ─── Field cluster (shared between desktop inline + mobile Sheet) ───

interface FilterFieldsProps {
  filters: CrewFilters
  sort: CrewSort
  filterOptions: CrewFilterOptions
  onLocationChange: (location: string | null) => void
  onFollowersChange: (min: number | null, max: number | null) => void
  onTagsChange: (tags: string[]) => void
  onJoinedRangeChange: (from: string | null, to: string | null) => void
  onAtRiskChange: (value: boolean) => void
  onMasterclassCompletionChange: (
    op: MasterclassCompletionOp | null,
    masterclassId: number | null,
  ) => void
  onSortChange: (sort: CrewSort) => void
}

function FilterFields({
  filters,
  sort,
  filterOptions,
  onLocationChange,
  onFollowersChange,
  onTagsChange,
  onJoinedRangeChange,
  onAtRiskChange,
  onMasterclassCompletionChange,
  onSortChange,
}: FilterFieldsProps) {
  return (
    <>
      <LocationField
        value={filters.location ?? null}
        options={filterOptions.locations}
        onChange={onLocationChange}
      />
      <FollowersField
        min={filters.followers_min}
        max={filters.followers_max}
        hint={filterOptions.followers_count}
        onChange={onFollowersChange}
      />
      <TagsField
        selected={filters.tags}
        options={filterOptions.tags}
        onChange={onTagsChange}
      />
      <JoinedField
        from={filters.joined_from}
        to={filters.joined_to}
        onChange={onJoinedRangeChange}
      />
      {/* OKS-58 — Masterclass-completion rule field. Sits between
          Joined and At-risk on the rail (UX Spec §2). Stages
          operator + id locally; commits both on Apply via the
          parent's `onMasterclassCompletionChange(op, id)` pair-emitter. */}
      <MasterclassCompletionField
        op={filters.masterclass_completion_op}
        masterclassId={filters.masterclass_completion_id}
        options={filterOptions.published_masterclasses}
        onChange={onMasterclassCompletionChange}
      />
      {/* OKS-27 — Pressable shadcn `Toggle` between Joined and Sort
          per UX V2 §Layout. `aria-pressed` reflects URL truth (the
          BE-echoed `filters.at_risk` boolean); instant URL update on
          toggle (no Apply button — single boolean). */}
      <AtRiskField pressed={filters.at_risk} onChange={onAtRiskChange} />
      <SortField sort={sort} onChange={onSortChange} />
    </>
  )
}

// ─── At-risk Toggle (OKS-27) ───────────────────────────────────────

function AtRiskField({
  pressed,
  onChange,
}: {
  pressed: boolean
  onChange: (value: boolean) => void
}) {
  const { t } = useTranslation()
  return (
    <Toggle
      variant="outline"
      // Base UI Toggle's `pressed` is the URL-derived truth — the parent
      // hook re-renders with the BE-echoed `filters.at_risk`. No local
      // optimistic state. OKS-625: Base UI passes `(pressed, eventDetails)`
      // to onPressedChange; strip the extra arg to keep onChange's clean
      // `(value: boolean)` contract.
      pressed={pressed}
      onPressedChange={(next) => onChange(next)}
      data-testid="crew-list-filter-at-risk"
      aria-label={t("crew.filter.at_risk")}
      aria-pressed={pressed}
    >
      {t("crew.filter.at_risk")}
    </Toggle>
  )
}

// ─── Location single-Select (OKS-348 composite-string) ───────────

function LocationField({
  value,
  options,
  onChange,
}: {
  value: string | null
  options: string[]
  onChange: (value: string | null) => void
}) {
  const { t } = useTranslation()
  // OKS-348 — Plain shadcn `<Select>` over the `string[]` composite
  // digest. `__all__` sentinel maps to `null` (clears the URL key).
  // Locked testid `crew-list-filter-location` carried forward.
  const selected = value && value.trim().length > 0 ? value : "__all__"

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor="crew-list-filter-location" className="sr-only">
        {t("crew.filters.location")}
      </Label>
      <Select
        items={[
          { value: "__all__", label: t("crew.filters.location") },
          ...options.map((option) => ({ value: option, label: option })),
        ]}
        value={selected}
        onValueChange={(v) => onChange(v === "__all__" ? null : v)}
      >
        <SelectTrigger
          id="crew-list-filter-location"
          data-testid="crew-list-filter-location"
          aria-label={t("crew.filters.location")}
          className="min-w-44"
        >
          <SelectValue placeholder={t("crew.filters.location")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">{t("crew.filters.location")}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

// ─── Followers Popover (min/max + Apply) ───────────────────────────

function FollowersField({
  min,
  max,
  hint,
  onChange,
}: {
  min: number | null
  max: number | null
  hint: { min: number; max: number } | null
  onChange: (min: number | null, max: number | null) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [stagedMin, setStagedMin] = useState<string>(min?.toString() ?? "")
  const [stagedMax, setStagedMax] = useState<string>(max?.toString() ?? "")

  function apply() {
    onChange(parseRange(stagedMin), parseRange(stagedMax))
    setOpen(false)
  }

  const isActive = min !== null || max !== null
  const trigger = (
    <PopoverTrigger
      render={
        <Button
          type="button"
          variant="outline"
          data-testid="crew-list-filter-followers"
          aria-label={t("crew.filters.followers")}
        >
          {t("crew.filters.followers")}
          {isActive && (
            <Badge variant="secondary" className="ml-2">
              {min ?? "—"}–{max ?? "—"}
            </Badge>
          )}
          <ChevronDown className="ml-1 size-3" aria-hidden="true" />
        </Button>
      }
    />
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {trigger}
      <PopoverContent className="w-72">
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="crew-followers-min">
                {t("crew.filters.followers_min")}
              </Label>
              <Input
                id="crew-followers-min"
                name="followers_min"
                type="number"
                inputMode="numeric"
                min={0}
                placeholder={hint ? String(hint.min) : "0"}
                data-testid="crew-list-filter-followers-min"
                value={stagedMin}
                onChange={(e) => setStagedMin(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="crew-followers-max">
                {t("crew.filters.followers_max")}
              </Label>
              <Input
                id="crew-followers-max"
                name="followers_max"
                type="number"
                inputMode="numeric"
                min={0}
                placeholder={hint ? String(hint.max) : ""}
                data-testid="crew-list-filter-followers-max"
                value={stagedMax}
                onChange={(e) => setStagedMax(e.target.value)}
              />
            </div>
          </div>
          <Button
            type="button"
            data-testid="crew-list-filter-followers-apply"
            onClick={apply}
          >
            {t("crew.filters.followers_apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function parseRange(value: string): number | null {
  if (value.trim() === "") return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

// ─── Tags multi-select Combobox ────────────────────────────────────

function TagsField({
  selected,
  options,
  onChange,
}: {
  selected: string[]
  options: string[]
  onChange: (tags: string[]) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const isActive = selected.length > 0

  function toggle(tag: string) {
    if (selected.includes(tag)) {
      onChange(selected.filter((entry) => entry !== tag))
    } else {
      onChange([...selected, tag])
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            data-testid="crew-list-filter-tags"
            aria-label={t("crew.filters.tags")}
          >
            {t("crew.filters.tags")}
            {isActive && (
              <Badge variant="secondary" className="ml-2">
                {selected.length}
              </Badge>
            )}
            <ChevronDown className="ml-1 size-3" aria-hidden="true" />
          </Button>
        }
      />
      <PopoverContent className="w-64 p-0">
        <Command>
          <CommandInput placeholder={t("crew.filters.tags_placeholder")} />
          <CommandList>
            <CommandEmpty>—</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isChecked = selected.includes(option)
                return (
                  <CommandItem
                    key={option}
                    onSelect={() => toggle(option)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 size-4",
                        isChecked ? "opacity-100" : "opacity-0",
                      )}
                      aria-hidden="true"
                    />
                    {option}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
        {/* Hidden inputs surface the wire-format `tags[]` literal for
            the F6 wire-format Vitest canary, even though the URL
            mutation flows via onChange + the URL hook. */}
        <span className="sr-only">
          {selected.map((tag) => (
            <input key={tag} type="hidden" name="tags[]" value={tag} readOnly />
          ))}
        </span>
      </PopoverContent>
    </Popover>
  )
}

// ─── Joined-date Popover (native <input type="date"> × 2 + Apply) ──

function JoinedField({
  from,
  to,
  onChange,
}: {
  from: string | null
  to: string | null
  onChange: (from: string | null, to: string | null) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [stagedFrom, setStagedFrom] = useState<string>(from ?? "")
  const [stagedTo, setStagedTo] = useState<string>(to ?? "")

  function apply() {
    onChange(stagedFrom || null, stagedTo || null)
    setOpen(false)
  }

  const isActive = from !== null || to !== null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            data-testid="crew-list-filter-joindate"
            aria-label={t("crew.filters.joined")}
          >
            {t("crew.filters.joined")}
            {isActive && (
              <Badge variant="secondary" className="ml-2">
                {from ?? "…"}–{to ?? "…"}
              </Badge>
            )}
            <ChevronDown className="ml-1 size-3" aria-hidden="true" />
          </Button>
        }
      />
      <PopoverContent className="w-72">
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="crew-joined-from">
                {t("crew.filters.joined_from")}
              </Label>
              <Input
                id="crew-joined-from"
                name="joined_from"
                type="date"
                data-testid="crew-list-filter-joindate-from"
                value={stagedFrom}
                onChange={(e) => setStagedFrom(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="crew-joined-to">
                {t("crew.filters.joined_to")}
              </Label>
              <Input
                id="crew-joined-to"
                name="joined_to"
                type="date"
                data-testid="crew-list-filter-joindate-to"
                value={stagedTo}
                onChange={(e) => setStagedTo(e.target.value)}
              />
            </div>
          </div>
          <Button
            type="button"
            data-testid="crew-list-filter-joindate-apply"
            onClick={apply}
          >
            {t("crew.filters.joined_apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ─── Sort Select (mobile fallback / always-on-rail surface) ────────

function SortField({
  sort,
  onChange,
}: {
  sort: CrewSort
  onChange: (sort: CrewSort) => void
}) {
  const { t } = useTranslation()
  const active = resolveCrewSortOption(sort)

  return (
    <Select
      items={CREW_SORT_OPTIONS.map((option) => ({
        value: option.value,
        label: t(`crew.filters.sort.${option.value}` as const),
      }))}
      value={active}
      onValueChange={(value) => {
        const match = CREW_SORT_OPTIONS.find((option) => option.value === value)
        if (match) onChange({ field: match.field, dir: match.dir })
      }}
    >
      <SelectTrigger
        data-testid="crew-list-sort"
        aria-label={t("crew.filters.sort_label")}
        className="min-w-40"
      >
        <SelectValue placeholder={t("crew.filters.sort_label")} />
      </SelectTrigger>
      <SelectContent>
        {CREW_SORT_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {t(`crew.filters.sort.${option.value}` as const)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

// Keep `ReactNode` imported if a downstream consumer adds slot props.
export type CrewFilterRailExtensions = ReactNode
